import { useEffect, useState, useCallback, useRef } from 'react';
import comms from '../components/comms';

const GPS_THROTTLE_MS = 30000; // 30 seconds between server GPS updates
const STALE_VEHICLE_MS = 120000; // Remove vehicles not heard from in 2 minutes

/**
 * Hook for managing route group GPS — join group room, send GPS,
 * listen for peer vehicles, and clean up stale entries.
 *
 * @param {Object} options
 * @param {Object} options.selectedVehicle  - Current vehicle { id, ... }
 * @param {Object} options.routeDetails     - Route with routeId + group IDs
 * @param {Object} options.fleetSettings    - Fleet config (unitId, etc.)
 * @param {string} [options.routeType]      - Override route type label
 *
 * @returns {{ groupVehicles: Map, sendGPS: (location) => void }}
 */
export default function useRouteGroupGPS({ selectedVehicle, routeDetails, fleetSettings, routeType: routeTypeOverride }) {
  const [groupVehicles, setGroupVehicles] = useState(new Map());

  const lastServerUpdateRef = useRef(0);
  const gpsIntervalRef = useRef(null);
  const lastLocationRef = useRef(null);
  const staleTimerRef = useRef(null);

  // Resolve routeGroupId from whichever field is available
  const routeGroupId =
    routeDetails?.FixedRouteGroupId ||
    routeDetails?.routeMaster?.RouteMasterGroupId ||
    routeDetails?.RouteMasterGroupId ||
    null;

  const routeType =
    routeTypeOverride ||
    (routeDetails?.FixedRouteGroupId ? 'fixedroute' : 'routemaster');

  const vehicleId = selectedVehicle?.id;
  const routeId = routeDetails?.routeId || routeDetails?.FixedRouteId;
  const unitId = fleetSettings?.unitId;

  // ---- Join / leave the group room ----
  useEffect(() => {
    if (!vehicleId || !routeGroupId || !unitId) return;

    comms.joinRouteGroup(unitId, vehicleId, routeId, routeType, routeGroupId);

    const socket = comms.getSocket();
    if (!socket) return;

    const handleGroupGPSUpdate = (gpsData) => {
      // Ignore our own echoed position
      if (gpsData.vehicleId === vehicleId) return;

      setGroupVehicles((prev) => {
        const next = new Map(prev);
        next.set(gpsData.vehicleId, {
          vehicleId: gpsData.vehicleId,
          routeId: gpsData.routeId,
          unitId: gpsData.unitId || gpsData.vehicleObject?.unitId,
          latitude: gpsData.latitude,
          longitude: gpsData.longitude,
          speed: gpsData.speed,
          timestamp: gpsData.timestamp,
          routeType: gpsData.routeType,
          vehicleObject: gpsData.vehicleObject,
        });
        return next;
      });
    };

    const handleVehicleJoinedGroup = (data) => {
      console.log('useRouteGroupGPS - vehicle joined group:', data.vehicleId);
    };

    const handleVehicleLeftGroup = (data) => {
      setGroupVehicles((prev) => {
        const next = new Map(prev);
        next.delete(data.vehicleId);
        return next;
      });
    };

    socket.on('groupGPSUpdate', handleGroupGPSUpdate);
    socket.on('vehicleJoinedGroup', handleVehicleJoinedGroup);
    socket.on('vehicleLeftGroup', handleVehicleLeftGroup);

    return () => {
      socket.off('groupGPSUpdate', handleGroupGPSUpdate);
      socket.off('vehicleJoinedGroup', handleVehicleJoinedGroup);
      socket.off('vehicleLeftGroup', handleVehicleLeftGroup);

      // Formally leave the room on cleanup
      comms.leaveRouteGroup(vehicleId, routeId, routeGroupId);
    };
  }, [vehicleId, routeId, routeGroupId, routeType, unitId]);

  // ---- Stale vehicle cleanup (every 60s, drop entries older than 2 min) ----
  useEffect(() => {
    staleTimerRef.current = setInterval(() => {
      const cutoff = Date.now() - STALE_VEHICLE_MS;
      setGroupVehicles((prev) => {
        let changed = false;
        const next = new Map();
        prev.forEach((v, k) => {
          const ts = v.timestamp ? new Date(v.timestamp).getTime() : 0;
          if (ts >= cutoff) {
            next.set(k, v);
          } else {
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 60000);

    return () => {
      if (staleTimerRef.current) clearInterval(staleTimerRef.current);
    };
  }, []);

  // ---- Throttled GPS send ----
  const sendGPS = useCallback(
    (location) => {
      if (!location) return;
      lastLocationRef.current = location;

      const now = Date.now();
      if (now - lastServerUpdateRef.current < GPS_THROTTLE_MS) return;
      if (!vehicleId || !routeGroupId) return;

      comms.sendGPSUpdate(
        location.lat,
        location.lng,
        0,
        vehicleId,
        routeId,
        routeType,
        routeGroupId,
        selectedVehicle,
      );

      // Legacy GPS for backward compat
      comms.sendSocket(
        'GPS|N ' + Math.abs(location.lat) + ',W ' + Math.abs(location.lng) + ',0',
      );

      lastServerUpdateRef.current = now;
    },
    [vehicleId, routeId, routeType, routeGroupId, selectedVehicle],
  );

  // ---- 30-second interval fallback (covers periods where no location events fire) ----
  useEffect(() => {
    if (!vehicleId || !routeGroupId) return;

    gpsIntervalRef.current = setInterval(() => {
      if (lastLocationRef.current) {
        sendGPS(lastLocationRef.current);
      }
    }, GPS_THROTTLE_MS);

    return () => {
      if (gpsIntervalRef.current) {
        clearInterval(gpsIntervalRef.current);
        gpsIntervalRef.current = null;
      }
    };
  }, [vehicleId, routeGroupId, sendGPS]);

  return { groupVehicles, sendGPS };
}
